import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';

import { useUpdateBundle } from '~/hooks/apis/bundle/useBundles';

import { Bundle } from '~/types/bundles/bundle';

interface Props {
  bundle: Bundle;
  open: boolean;
  onClose: () => void;
}

export default function RenameBundleDialog({ bundle, open, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [name, setName] = useState(bundle.name);
  const updateBundle = useUpdateBundle(bundle.id);

  useEffect(() => {
    if (open) setName(bundle.name);
  }, [open, bundle.name]);

  const trimmed = name.trim();

  const handleClose = () => {
    updateBundle.reset();
    onClose();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      await updateBundle.mutateAsync({ name: trimmed });
      enqueueSnackbar('Bundle renamed', { variant: 'success' });
      handleClose();
    } catch {
      // The mutation error is shown in the dialog.
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit}>
        <DialogTitle>Rename bundle</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            inputProps={{ maxLength: 255 }}
            sx={{ mt: 1 }}
            required
            fullWidth
            autoFocus
          />
          {!!updateBundle.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {updateBundle.error.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!trimmed || trimmed === bundle.name || updateBundle.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
